/**
 * SOC dynamic price tiers in station notes — same rules as admin-portal/dynamic_price.py.
 * Example: SoC до 80% - 16.99 грн
 */

const SOC_TIER_LINE_RE = /soc\s+до\s+(\d+)\s*%\s*-\s*(\d+(?:[.,]\d+)?)\s*грн/gi;

/** Single-line test (for filtering user-visible notes). */
const SOC_TIER_LINE_ANCHORED_RE = /^soc\s+до\s+\d+\s*%\s*-\s*\d+(?:[.,]\d+)?\s*грн\s*$/i;

const BATTERY_SECTION_COUNT = 5;

export function parseSocTiersFromNotes(text) {
  if (text == null || typeof text !== 'string' || !text.trim()) {
    return [];
  }
  const byPct = new Map();
  let match = SOC_TIER_LINE_RE.exec(text);
  while (match) {
    const pct = Number.parseInt(match[1], 10);
    const price = Number.parseFloat(String(match[2]).replace(',', '.'));
    if (Number.isFinite(pct) && pct >= 0 && pct <= 100 && Number.isFinite(price) && price >= 0) {
      byPct.set(pct, {
        percent: pct,
        priceUah: price,
        label: `SoC до ${pct}% - ${price.toFixed(2)} грн`,
      });
    }
    match = SOC_TIER_LINE_RE.exec(text);
  }
  return [...byPct.values()].sort((a, b) => a.percent - b.percent);
}

/** Remove SOC dynamic price lines from notes shown to drivers (prices live on battery UI). */
export function stripSocTierLinesFromNotes(text) {
  if (text == null || typeof text !== 'string') return '';
  // Drop whole SoC-only lines and inline "SoC до X% - Y грн" fragments on mixed lines.
  const withoutInline = text.replace(/soc\s+до\s+\d+\s*%\s*-\s*\d+(?:[.,]\d+)?\s*грн/gi, ' ');
  const lines = withoutInline.split(/\r?\n/).filter(line => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    return !SOC_TIER_LINE_ANCHORED_RE.test(trimmed);
  });
  return lines
    .join('\n')
    .replace(/[^\S\n]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** notes_ua → notes_en → name (first field with SOC tiers). */
export function getSocPriceTiersFromStation(station) {
  if (!station) return [];
  for (const notes of [station.notesUa, station.notesEn, station.name]) {
    const tiers = parseSocTiersFromNotes(notes);
    if (tiers.length) return tiers;
  }
  return [];
}

/** Map SOC threshold % to 5-segment battery row (0 = bottom, 4 = top). */
export function socTierBatterySectionIndex(percent) {
  return Math.min(BATTERY_SECTION_COUNT - 1, Math.max(0, Math.ceil(percent / 20) - 1));
}

/** Price band for tier label color: cheap (green), avg (yellow), expensive (red). */
export function socTierPriceBand(priceUah, tiers) {
  if (!tiers?.length || priceUah == null || !Number.isFinite(Number(priceUah))) {
    return 'avg';
  }
  const uniquePrices = [...new Set(tiers.map(t => t.priceUah))].sort((a, b) => a - b);
  if (uniquePrices.length === 1) return 'cheap';
  const rank = uniquePrices.indexOf(priceUah);
  if (rank <= 0) return 'cheap';
  if (rank >= uniquePrices.length - 1) return 'expensive';
  return 'avg';
}

/** Fill colors for tier sections and price stickers (cheap / avg / expensive). */
export const SOC_TIER_BAND_FILL = {
  // Keep the lowest-price tier green, independently of the port online status color.
  cheap: '#1f9d4a',
  avg: '#facc15',
  expensive: '#f87171',
};

/** Text + glow tokens for eco-card power/status when a SOC price tier is active. */
export const SOC_TIER_BAND_TEXT = {
  cheap: {
    color: '#d4ffe8',
    shadow: '0 0 14px rgba(110, 231, 160, 0.55), 0 0 4px rgba(82, 216, 138, 0.35)',
  },
  avg: {
    color: '#ffe066',
    shadow: '0 0 14px rgba(255, 224, 102, 0.55), 0 0 4px rgba(255, 201, 74, 0.35)',
  },
  expensive: {
    color: '#ffc9c9',
    shadow: '0 0 14px rgba(248, 113, 113, 0.55), 0 0 4px rgba(239, 68, 68, 0.35)',
  },
};

/** Band for the active SOC tier (same rule as blinking battery section). */
export function getActiveSocPriceBand(tiers, socPercent) {
  const active = getActiveSocPriceTier(tiers, socPercent);
  if (!active) return null;
  return socTierPriceBand(active.priceUah, tiers);
}

const DAM_BASE_PRICE_RE = /(\d+(?:[.,]\d+)?)\s*[-–—]\s*тариф\s+базовий/i;
const DAM_RDN_PRICE_RE = /(\d+(?:[.,]\d+)?)\s*[-–—]\s*тариф\s+рдн/i;

/** Base / DAM (РДН) prices from station notes when SOC tiers are absent. */
export function getDamPricesFromStation(station) {
  if (!station) return null;
  for (const notes of [station.notesUa, station.notesEn, station.name]) {
    if (notes == null || typeof notes !== 'string' || !notes.trim()) continue;
    const baseMatch = notes.match(DAM_BASE_PRICE_RE);
    const damMatch = notes.match(DAM_RDN_PRICE_RE);
    if (!baseMatch || !damMatch) continue;
    const baseUah = Number.parseFloat(String(baseMatch[1]).replace(',', '.'));
    const damUah = Number.parseFloat(String(damMatch[1]).replace(',', '.'));
    if (!Number.isFinite(baseUah) || !Number.isFinite(damUah)) continue;
    return { baseUah, damUah };
  }
  return null;
}

/** Green when listed price is the cheap DAM tariff; red for base. */
function resolveDamPriceStickerBand(station, costPerKwt) {
  const dam = getDamPricesFromStation(station);
  if (!dam || costPerKwt == null || !Number.isFinite(Number(costPerKwt))) {
    return null;
  }
  const priceUah = Number((Number(costPerKwt) / 100).toFixed(2));
  if (Math.abs(priceUah - dam.damUah) < 0.001) return 'cheap';
  if (Math.abs(priceUah - dam.baseUah) < 0.001) return 'expensive';
  if (priceUah <= dam.damUah) return 'cheap';
  if (priceUah >= dam.baseUah) return 'expensive';
  return 'avg';
}

/** Resolve sticker color band: prefer active SOC tier, else DAM cheap/base, else match displayed price to tiers. */
export function resolvePortPriceStickerBand({ station, socPercent, costPerKwt }) {
  const tiers = getSocPriceTiersFromStation(station);
  if (tiers.length) {
    if (socPercent != null && Number.isFinite(Number(socPercent))) {
      return getActiveSocPriceBand(tiers, socPercent);
    }

    if (costPerKwt == null || !Number.isFinite(Number(costPerKwt))) {
      return null;
    }

    const priceUah = Number((Number(costPerKwt) / 100).toFixed(2));
    const matchedTier = tiers.find(tier => Math.abs(tier.priceUah - priceUah) < 0.001);
    return socTierPriceBand(matchedTier?.priceUah ?? priceUah, tiers);
  }

  return resolveDamPriceStickerBand(station, costPerKwt);
}

/** Active tier: smallest threshold where current_soc <= X; fallback = max threshold tier. */
export function getActiveSocPriceTier(tiers, socPercent) {
  if (!tiers?.length || socPercent == null || !Number.isFinite(Number(socPercent))) {
    return null;
  }
  const soc = Math.min(100, Math.max(0, Number(socPercent)));
  for (const tier of tiers) {
    if (soc <= tier.percent) return tier;
  }
  return tiers[tiers.length - 1];
}

/** Build battery sections from SOC price tiers (one section per tariff, height ∝ SOC band). */
export function buildSocTierBatterySections(tiers, socPercent) {
  if (!tiers?.length) return [];
  const soc = Math.min(100, Math.max(0, Number(socPercent) || 0));
  const sorted = [...tiers].sort((a, b) => a.percent - b.percent);
  const active = getActiveSocPriceTier(tiers, soc);
  let prevPct = 0;
  return sorted
    .map(tier => {
      const bandMin = prevPct;
      const bandMax = tier.percent;
      prevPct = tier.percent;
      const bandSpan = Math.max(0, bandMax - bandMin);
      const fillPct = bandSpan <= 0 ? 0 : Math.min(100, Math.max(0, ((soc - bandMin) / bandSpan) * 100));
      return {
        tier,
        bandMin,
        bandMax,
        bandSpan,
        fillPct,
        isActive: active?.percent === tier.percent,
      };
    })
    .filter(section => section.bandSpan > 0);
}

/** Compact price label for in-battery tier sections. */
export function formatSocTierPriceLabel(priceUah) {
  const price = Number(priceUah);
  if (!Number.isFinite(price)) return '—';
  if (Math.abs(price - Math.round(price)) < 0.001) {
    return String(Math.round(price));
  }
  return price.toFixed(2);
}

export { BATTERY_SECTION_COUNT };
